import { AsyncPipe } from '@angular/common';
import type { TrackByFunction } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { ControlValueAccessorDirective } from '../../directives/control-value-accessor.directive';
import { MatTooltipModule } from '@angular/material/tooltip';

interface ChipState {
  label: string;
  value: string;
  selected: boolean;
  disabled: boolean;
  tooltip?: string;
}

@Component({
  selector: 'ui-single-select-chips-toggle',
  standalone: true,
  imports: [AsyncPipe, MatChipsModule, MatTooltipModule],
  templateUrl: './single-select-chips-toggle.component.html',
  styleUrls: ['./single-select-chips-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleSelectChipsToggleComponent),
      multi: true,
    },
  ],
})
export class SingleSelectChipsToggleComponent extends ControlValueAccessorDirective<string> {
  trackByValue: TrackByFunction<ChipState> = (_, c) => c.value;

  value$ = new BehaviorSubject<string>('');
  options$ = new BehaviorSubject<ChipsToggleSingleSelectOption[]>([]);

  @Input()
  set options(options: ChipsToggleSingleSelectOption[] | undefined | null) {
    this.options$.next(options || []);
  }

  disabledOptions$ = new BehaviorSubject<Array<{ value: string; tooltip?: string }>>([]);

  @Input()
  set disabledOptions(options: Array<{ value: string; tooltip?: string }> | null) {
    this.disabledOptions$.next(options ?? []);
  }

  constructor() {
    super();
  }

  state$ = combineLatest([this.options$, this.value$, this.disabled$, this.disabledOptions$]).pipe(
    map(([options, value, disabled, disabledOptions]) => {
      return options.map(o => {
        const disabledOption = disabledOptions.find(item => item.value === o.value);
        return {
          ...o,
          disabled: disabled || !!disabledOption || !!o.disabled,
          tooltip: disabledOption?.tooltip,
          selected: Boolean(value === o.value),
        };
      });
    })
  );

  override writeValue(value: string): void {
    super.writeValue(value);
    this.value$.next(value);
  }

  toggle(value: string): void {
    this.value$.next(value);
    this.onTouched();
    this.onChange(this.value$.value);
  }
}

export interface ChipsToggleSingleSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}
